// ai-chat-store.ts — Device-local localStorage store for LifeAI chat sessions.
// NOT synced to Supabase / LifeOsState — browser-only.
// Quota-safe: every read/write wrapped in try/catch.
// Mirrors the pattern in src/lib/app-prefs.ts.

import type { AiContent } from "./ai-client";
import type { UiMessage } from "../../bento/assistant-message-list";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ChatSession {
  id: string;
  title: string;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  messages: UiMessage[];
  contents: AiContent[];
}

// ── Storage keys ──────────────────────────────────────────────────────────────

const SESSIONS_KEY = "personal-life-os:ai-chats";
const ACTIVE_KEY = "personal-life-os:ai-active-chat";
const MAX_SESSIONS = 30;

// ── Helpers ───────────────────────────────────────────────────────────────────

function readSessions(): ChatSession[] {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as ChatSession[];
  } catch {
    return [];
  }
}

function writeSessions(sessions: ChatSession[]): void {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  } catch {
    // Quota exceeded or private browsing — ignore.
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * All sessions sorted by updatedAt descending (newest first).
 */
export function listSessions(): ChatSession[] {
  return readSessions().sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

/**
 * Find a single session by id. Returns null when not found or on error.
 */
export function getSession(id: string): ChatSession | null {
  try {
    const sessions = readSessions();
    return sessions.find((s) => s.id === id) ?? null;
  } catch {
    return null;
  }
}

/**
 * Insert or replace a session.
 * Caps stored sessions at MAX_SESSIONS — drops the oldest by updatedAt.
 * Silently ignores quota errors.
 */
export function upsertSession(session: ChatSession): void {
  try {
    const sessions = readSessions().filter((s) => s.id !== session.id);
    sessions.push(session);

    // Keep only the newest MAX_SESSIONS
    if (sessions.length > MAX_SESSIONS) {
      sessions.sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      sessions.splice(MAX_SESSIONS);
    }

    writeSessions(sessions);
  } catch {
    // Quota exceeded — ignore.
  }
}

/**
 * Remove a session by id. No-op if not found.
 */
export function deleteSession(id: string): void {
  try {
    const sessions = readSessions().filter((s) => s.id !== id);
    writeSessions(sessions);
  } catch {
    // Ignore.
  }
}

/**
 * Wipe ALL chat sessions + the active pointer from this device.
 * Called on sign-out so one user's chats (which echo back their personal data)
 * never leak to the next account signed in on the same browser/device.
 */
export function clearAllSessions(): void {
  try {
    localStorage.removeItem(SESSIONS_KEY);
    localStorage.removeItem(ACTIVE_KEY);
  } catch {
    // Ignore.
  }
}

// ── Active session pointer ─────────────────────────────────────────────────────

/**
 * Returns the id of the active (last open) session, or null.
 */
export function getActiveId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

/**
 * Persist the active session id. Pass null to clear.
 */
export function setActiveId(id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(ACTIVE_KEY);
    } else {
      localStorage.setItem(ACTIVE_KEY, id);
    }
  } catch {
    // Ignore.
  }
}

// ── Factory ───────────────────────────────────────────────────────────────────

/**
 * Create a new in-memory ChatSession (NOT persisted until first message).
 * Uses crypto.randomUUID when available; falls back to timestamp+random.
 */
export function createSession(): ChatSession {
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const now = new Date().toISOString();
  return {
    id,
    title: "Cuộc trò chuyện mới",
    createdAt: now,
    updatedAt: now,
    messages: [],
    contents: [],
  };
}

/**
 * Derive a human-readable title from the message list.
 * Uses the first user message trimmed to ~40 chars; falls back to default title.
 */
export function deriveTitle(messages: UiMessage[]): string {
  const first = messages.find((m) => m.kind === "user");
  if (!first || first.kind !== "user") return "Cuộc trò chuyện mới";
  const text = first.text.trim();
  return text.length <= 40 ? text : `${text.slice(0, 40)}…`;
}
